import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { easeOut, Magnetic } from "./Shared";

const pills = ["Owner-Side Only", "Fee-for-Service", "Data-Driven"];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.06em] -mb-[0.06em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%", rotate: 3 }}
      animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.2, delay, ease: easeOut }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  return (
    <section data-testid="hero-section" className="relative border-b border-black/15 overflow-hidden">
      <div className="max-w-[1100px] mx-auto px-6 pt-44 md:pt-56 pb-24 md:pb-36 text-center flex flex-col items-center">
        <motion.span
          data-testid="hero-badge"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: easeOut }}
          className="inline-block border border-black/20 rounded-full px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.3em] text-black/60"
        >
          Owner-Side Property Consulting · London &amp; UK
        </motion.span>

        <h1
          data-testid="hero-headline"
          className="mt-10 font-serif text-5xl sm:text-7xl lg:text-[6.2rem] leading-[0.98] tracking-tighter text-[#0A0A0A]"
        >
          <Line delay={1.0}>Cross-border clarity</Line>
          <Line delay={1.15}>for owners of</Line>
          <Line delay={1.3}>
            <em className="font-light">prime UK property.</em>
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.65, ease: easeOut }}
          className="mt-10 max-w-2xl mx-auto text-base lg:text-lg leading-relaxed text-black/60 font-light"
        >
          I Putu Adi Semarada LLC is an independent property consultancy for global owners of prime UK real estate.
          We advise on yield, refurbishment, and portfolio operation.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.85, ease: easeOut }}
          className="mt-12 flex flex-wrap items-center justify-center gap-5"
        >
          <Magnetic>
            <a
              href="#practice"
              data-testid="hero-cta-explore"
              className="inline-flex items-center gap-3 rounded-full bg-[#0A0A0A] text-[#F4F3EF] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-[#D4512D]"
            >
              Explore the Advisory <ArrowUpRight size={15} strokeWidth={1.5} />
            </a>
          </Magnetic>
          <Magnetic>
            <a
              href="#contact"
              data-testid="hero-cta-consultation"
              className="inline-flex items-center gap-3 rounded-full border border-black/25 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-black/80 transition-colors duration-300 hover:bg-[#0A0A0A] hover:text-[#F4F3EF] hover:border-[#0A0A0A]"
            >
              Book a Consultation
            </a>
          </Magnetic>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 2.1 }}
          className="mt-12 flex flex-wrap items-center justify-center gap-3"
        >
          {pills.map((p) => (
            <span
              key={p}
              data-testid={`hero-pill-${p.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-black/50 border border-black/15 rounded-full px-5 py-2"
            >
              {p}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
